import { inject, Injectable } from "@angular/core";

import { IPagedGridSettings, IQueryPageRequest, IQuerySchemaColumn } from "@main/reporting.v3/core/reporting-models";
import { VariableRuleService } from "@main/reporting.v3/core/variables/variable-rule.service";
import { QUERY_HUB } from "@main/reporting.v3/core/query-hub/constants/query-hub.constants";
import { IQueryVariables } from "@main/reporting.v3/core/variables/variable-models";
import { QueryData, QueryHub } from "@main/reporting.v3/core/query-hub/models";

import {
    HtmlContext,
    HtmlSequence,
    HtmlDirective,
    HtmlDirectiveEvaluator,
    HtmlDirectiveEvaluatorSettings,
} from "@main/reporting.v3/widget.html/models";
import {
    HtmlBaseExpressionsArgumentService
} from "@main/reporting.v3/widget.html/services/html-expressions/html-base-expressions-argument.service";


@Injectable()
export class HtmlQueryDirectiveService implements HtmlDirectiveEvaluator {

    private argumentService: HtmlBaseExpressionsArgumentService = inject(HtmlBaseExpressionsArgumentService);
    private variableRuleService: VariableRuleService = inject(VariableRuleService);

    private queryHub: QueryHub = inject(QUERY_HUB);

    public readonly directive: string = "query";

    public async evaluate(directive: HtmlDirective, settings: HtmlDirectiveEvaluatorSettings): Promise<any> {
        if (!directive || directive.name !== this.directive || !settings) return null;

        const args: HtmlSequence[] = directive.arguments.slice(0, 4);
        const params: any[] = await this.argumentService.evaluateAll(args, settings);
        const name: string = this.argumentService.toString(params[0]);
        const column: string = this.argumentService.toString(params[1]);
        const take: number = this.argumentService.toNumber(params[2]);
        const skip: number = this.argumentService.toNumber(params[3]);

        // Ensure query name is passed and it is valid.
        if (!name?.length) return null;

        return this.query(settings.context, name, column, take, skip);
    }

    public async query(context: HtmlContext, name: string, column?: string, take?: number, skip?: number): Promise<any> {
        try {
            if (!context || !name?.length) return null;

            // Get the query data (column metadata + data rows) from the query hub.
            const request: IQueryPageRequest = this.getPageRequest(context, take, skip);
            const queryData: QueryData = await this.queryHub.requestQueryByName(name, request);

            // If the query has no columns (error) nor data rows (empty), return null as result.
            if (!queryData?.columns?.length || !queryData?.dataRows?.length) return null;

            // If column was explicitly not specified (null was used as column name), return data rows objects.
            if (column === null) return queryData.dataRows;

            // If column name is specified, find a column with this name as query alias or database column name.
            // Otherwise, find the first non-key column if exists, or simply the first column if not.
            const schemaColumn: IQuerySchemaColumn = !!column?.length
                ? this.getColumnByName(queryData, column)
                : this.getNonKeyColumnOrDefault(queryData);

            if (!schemaColumn?.queryAlias?.length) return null;

            // Get the value corresponding to the specified column on each row.
            const results: any[] = queryData.dataRows.map(row => row[schemaColumn.queryAlias]);

            // If there is only one record, return the record directly.
            return results.length > 1 ? results : results[0];

        } catch (error) {
            return null;
        }
    }

    private getPageRequest(context: HtmlContext, take?: number, skip?: number): IQueryPageRequest {
        if (!context) return null;

        const gridSettings: IPagedGridSettings = {
            // If take was explicitly not specified (null was used as take parameter),
            // it is defaulted to a number large enough to retrieve all query rows.
            take: take === null ? 1_000_000_000 : take ?? 1,

            // Skip parameter always defaults to zero despite it is explicitly
            // specified as null or if it is just missing.
            skip: skip ?? 0,
        };

        const queryVariables: IQueryVariables = {
            variables: [
                ...this.variableRuleService.toEmittedVariables(context.staticVariables),
                ...context.emittedVariables,
            ]
        };

        return { gridSettings, queryVariables };
    }

    private getColumnByName(queryData: QueryData, columnName: string): IQuerySchemaColumn {
        if (!queryData?.columns?.length) return null;

        // If column name is specified, find a column with this name as query alias.
        // If it is not fund, find a column with this name as database column name.
        return queryData.columns.find(column => column.queryAlias.localeCompare(columnName) === 0)
            ?? queryData.columns.find(column => column.columnName?.localeCompare(columnName) === 0);
    }

    private getNonKeyColumnOrDefault(queryData: QueryData): IQuerySchemaColumn {
        if (!queryData?.columns?.length) return null;

        // Get first non-key column if there is any. Otherwise, return first column.
        return queryData.columns.find(schemaColumn => !schemaColumn.isKey) ?? queryData.columns[0];
    }
}